import ClearIcon from '@mui/icons-material/Clear';
import SearchIcon from '@mui/icons-material/Search';
import SyncAltIcon from '@mui/icons-material/SyncAlt';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import {
	DataGrid,
	GridColDef,
	GridRowParams,
	GridRowSelectionModel,
	GridToolbarContainer,
} from '@mui/x-data-grid';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import React from 'react';
import UserContext from '../../../context/userContext';
import { successToast } from '../../Toast';
import { MigrateApi, RolesApi } from './api';
import { ConfirmationModal } from './components';
import { GetMigratableTenantRequest, MigratableTenant } from './types';

interface QuickSearchToolbarProps {
	clearSearch: () => void;
	onChange: () => void;
	value: string;
	disabled?: boolean;
	onButtonClick: () => void;
}

function escapeRegExp(value: string): string {
	return value.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, '\\$&');
}

function QuickSearchToolbar(props: QuickSearchToolbarProps) {
	return (
		<GridToolbarContainer>
			<Grid
				container
				direction='row'
				justifyContent='space-between'
				alignItems='center'
				sx={{ pl: 1, pr: 1 }}
			>
				<Grid item>
					<Button
						variant='outlined'
						color='secondary'
						startIcon={<SyncAltIcon />}
						disabled={props.disabled}
						onClick={props.onButtonClick}
					>
						Migrate Tenants
					</Button>
				</Grid>
				<Grid item>
					<TextField
						variant='outlined'
						value={props.value}
						onChange={props.onChange}
						size='small'
						placeholder='Search…'
						InputProps={{
							startAdornment: <SearchIcon fontSize='small' />,
							endAdornment: (
								<IconButton
									title='Clear'
									aria-label='Clear'
									size='small'
									style={{ visibility: props.value ? 'visible' : 'hidden' }}
									onClick={props.clearSearch}
								>
									<ClearIcon fontSize='small' />
								</IconButton>
							),
						}}
						sx={{
							width: {
								xs: 1,
								sm: 'auto',
							},
							m: (theme) => theme.spacing(1, 0.5, 1.5),
							'& .MuiSvgIcon-root': {
								mr: 0.5,
							},
							'& .MuiInput-underline:before': {
								borderBottom: 1,
								borderColor: 'divider',
							},
						}}
					/>
				</Grid>
			</Grid>
		</GridToolbarContainer>
	);
}

const AdministrationMigrate = () => {
	const { selectedUserRole: user } = React.useContext(UserContext);
	const queryClient = useQueryClient();
	const [confirmMigrateModal, setConfirmMigrateModal] = React.useState(false);
	const [showPassword, setShowPassword] = React.useState(false);
	const [rows, setRows] = React.useState<MigratableTenant[]>([]);
	const [searchText, setSearchText] = React.useState('');
	const [selectionModel, setSelectionModel] =
		React.useState<GridRowSelectionModel>([]);
	const [formState, setFormState] = React.useState<GetMigratableTenantRequest>({
		username: '',
		password: '',
	});
	const handleClickShowPassword = () => {
		setShowPassword(!showPassword);
	};

	const handleMouseDownPassword = (
		event: React.MouseEvent<HTMLButtonElement>,
	) => {
		event.preventDefault();
	};
	const ROLES_KEY = RolesApi.ROLES_KEY + '-' + user.namespace;
	const updateUserMutation = useMutation(MigrateApi.getMigratableTenants);
	const migrateTenantMutation = useMutation(MigrateApi.migrateTenant, {
		onSuccess: () => {
			setSearchText('');
			updateUserMutation.mutate(formState);
			queryClient.invalidateQueries([ROLES_KEY]);
			successToast(
				'Successfully migrated ' + selectionModel.length + ' tenant(s)',
			);
		},
	});

	React.useEffect(() => {
		setRows(updateUserMutation.data ? updateUserMutation.data : []);
		setSearchText('');
	}, [updateUserMutation.data]);

	const [formTouched, setFormTouched] = React.useState({
		username: false,
		password: false,
	});

	const [formErrors, setFormErrors] = React.useState({
		username: '',
		password: '',
	});

	const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
		event.preventDefault();
		setFormTouched({
			username: true,
			password: true,
		});

		if (formState.username === '' || formState.password === '') {
			setFormErrors({
				username: formState.username === '' ? 'Required' : '',
				password: formState.password === '' ? 'Required' : '',
			});
		} else {
			updateUserMutation.mutate(formState);
		}
	};

	const handleFormChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const { name, value } = event.target;

		if (value === '') {
			setFormErrors({
				...formErrors,
				[name]: 'This field is required',
			});
		} else {
			setFormErrors({
				...formErrors,
				[name]: '',
			});
		}

		setFormTouched((prevState) => ({
			...prevState,
			[name]: true,
		}));

		setFormState((prevState) => ({
			...prevState,
			[name]: value,
		}));
	};

	const requestSearch = (searchValue: string) => {
		setSearchText(searchValue);
		const searchRegex = new RegExp(escapeRegExp(searchValue), 'i');
		const filteredRows = updateUserMutation.data.filter((row: any) => {
			return Object.keys(row).some((field: any) => {
				return searchRegex.test(row[field].toString());
			});
		});
		setRows(filteredRows);
	};

	const columns: GridColDef[] = [
		{
			field: 'name',
			headerName: 'Tenant Name',
			width: 220,
		},
		{
			field: 'hostname',
			headerName: 'Hostname',
			width: 220,
		},
		{
			field: 'locator',
			headerName: 'Locator',
			width: 220,
		},
		{
			field: 'status',
			headerName: 'Status',
			width: 220,
		},
	];

	return (
		<Grid container spacing={2} height='90%'>
			<Grid item xs={12} sm={12} md={5} lg={3}>
				<Card sx={{ p: 2 }}>
					<Typography variant='h6' sx={{ pb: 2 }}>
						Migrate
					</Typography>
					<Typography variant='body2'>
						To start a migration, enter a username and password of an account
						you want to convert.
					</Typography>
					<Box
						component='form'
						onSubmit={(event: React.FormEvent<HTMLFormElement>) =>
							handleSubmit(event)
						}
					>
						<TextField
							error={formTouched.username && formErrors.username !== ''}
							helperText={formTouched.username && formErrors.username}
							label='Username'
							variant='outlined'
							fullWidth
							margin='normal'
							name='username'
							value={formState.username}
							disabled={updateUserMutation.isLoading}
							onChange={handleFormChange}
						/>
						<TextField
							error={formTouched.password && formErrors.password !== ''}
							helperText={formTouched.password && formErrors.password}
							label='Password'
							variant='outlined'
							fullWidth
							margin='normal'
							name='password'
							type={showPassword ? 'text' : 'password'}
							InputProps={{
								endAdornment: (
									<InputAdornment position='end'>
										<IconButton
											aria-label='toggle password visibility'
											onClick={handleClickShowPassword}
											onMouseDown={handleMouseDownPassword}
											edge='end'
										>
											{showPassword ? <VisibilityOff /> : <Visibility />}
										</IconButton>
									</InputAdornment>
								),
							}}
							value={formState.password}
							disabled={updateUserMutation.isLoading}
							onChange={handleFormChange}
						/>
						<Button
							variant='contained'
							color='secondary'
							fullWidth
							type='submit'
							disabled={updateUserMutation.isLoading}
							sx={{ mt: 2 }}
						>
							Find Associated Tenants
						</Button>
					</Box>
				</Card>
			</Grid>
			<Grid item xs={12} sm={12} md={7} lg={9}>
				<Card sx={{ flexGrow: 1, height: 1 }}>
					<DataGrid
						getRowId={(row: any) => row['hostname']}
						checkboxSelection
						onRowSelectionModelChange={(newSelectionModel) => {
							setSelectionModel(newSelectionModel);
						}}
						rowSelectionModel={selectionModel}
						columns={columns}
						loading={updateUserMutation.isLoading}
						rows={rows}
						isRowSelectable={(params: GridRowParams) =>
							params.row.status === 'Not Migrated'
						}
						components={{
							Toolbar: QuickSearchToolbar,
						}}
						componentsProps={{
							toolbar: {
								value: searchText,
								onChange: (event: React.ChangeEvent<HTMLInputElement>) =>
									requestSearch(event.target.value),
								clearSearch: () => requestSearch(''),
								onButtonClick: () => setConfirmMigrateModal(true),
								disabled:
									updateUserMutation.isLoading ||
									!updateUserMutation.data ||
									!updateUserMutation.data.length ||
									!selectionModel.length,
							},
						}}
					/>
				</Card>
			</Grid>
			<ConfirmationModal
				open={confirmMigrateModal}
				headerText='Migrate Tenants'
				bodyText='Are you sure you want to migrate the selected tenants?'
				confirmButtonText='Migrate'
				confirmButtonColor='secondary'
				handleClose={() => setConfirmMigrateModal(false)}
				handleSubmit={() => {
					migrateTenantMutation.mutate({
						authCredentials: formState,
						tenantHostnames: selectionModel as string[],
					});
					setConfirmMigrateModal(false);
				}}
			/>
		</Grid>
	);
};

export default AdministrationMigrate;
